// De woorden en kleine rekenregels van de Trends-tab in de nieuwe weergave.
// De assen en sorteringen zijn die van het oude scherm (pages/TrendsPage.tsx),
// de API-waarden blijven gelijk; alleen de woorden op het scherm zijn korter.

import type { Trend } from "../../contracts";
import type { BronItem, Label } from "../../stijl";
import { naarTab } from "../../lib/naarTab";
import { vastePropositie } from "../../lib/sectorkeuze";
import { mistTekst } from "../../components/Bewijs";
import { samenvoegingVan, samenvoegToon } from "./samenvoeging";

export type Bewijsregel = Trend["evidence"][number];

export const PAGINA = 15;

// ── Assen ──────────────────────────────────────────────────────────────────

export type Soort = "vakgebied" | "arbeidsmarktobservatie" | "";
export const SOORTEN: { waarde: Soort; label: string; uitleg: string }[] = [
  { waarde: "vakgebied", label: "Vaktrends", uitleg: "Wat er verandert in het werk zelf." },
  { waarde: "arbeidsmarktobservatie", label: "Arbeidsmarkt", uitleg: "Wat er verandert aan de markt eromheen: vraag, aanbod, krapte, beleid." },
  { waarde: "", label: "Alles", uitleg: "Beide soorten samen, zoals de chat ze telt." },
];

export const SIGNALEN: { waarde: string; label: string }[] = [
  { waarde: "sterk", label: "Sterk (8+)" },
  { waarde: "gemiddeld", label: "Gemiddeld (6–8)" },
  { waarde: "zwak", label: "Zwak (< 6)" },
];

export const NIVEAUS: { waarde: "Macro" | "National" | "Sector"; label: string; kort: string; uitleg: string }[] = [
  { waarde: "Macro", label: "Internationaal", kort: "internationaal", uitleg: "Een internationaal beeld (EU, OESO, mondiaal)." },
  { waarde: "National", label: "Nederland", kort: "Nederland", uitleg: "Gaat over Nederland als geheel." },
  { waarde: "Sector", label: "Sector of beroep", kort: "sector", uitleg: "Over één bedrijfstak, regio of beroepsgroep: het meest specifieke niveau." },
];
export const niveauWoord = (l: string): string => NIVEAUS.find((n) => n.waarde === l)?.kort ?? l;

export type Detail = "super" | "kern" | "alles";
export const DETAILS: { waarde: Detail; label: string; uitleg: string }[] = [
  { waarde: "super", label: "Alleen hoofdtrends", uitleg: "Alleen trends die andere trends bundelen; de rest zit eronder." },
  { waarde: "kern", label: "Kernbeeld", uitleg: "Elke dragende trend als eigen regel; onderbouwing ingeklapt." },
  { waarde: "alles", label: "Alles uitgeklapt", uitleg: "Ook de onderbouwende trends staan open onder hun hoofdtrend." },
];

export const SORTERINGEN: { waarde: string; label: string; uitleg: string }[] = [
  { waarde: "uitgelicht", label: "Uitgelicht", uitleg: "Bewijskracht, breedte, versheid en beweging samen." },
  { waarde: "trending", label: "Trending", uitleg: "Hoe sterk het signaal nu is." },
  { waarde: "impact", label: "Impact", uitleg: "Hoe ingrijpend het voor het werk is." },
  { waarde: "urgentie", label: "Urgentie", uitleg: "Hoe snel het speelt." },
  { waarde: "bronnen", label: "Meeste bronnen", uitleg: "Hoeveel onafhankelijke bronnen het dragen." },
  { waarde: "nieuwste", label: "Nieuwste bewijs", uitleg: "Waar het jongste bewijs onder ligt." },
];
/** Methode v3: "Meeste bronnen" wordt "Sterkste bewijs" (sleutel `bewijs`). */
export const sorteringen = (v3: boolean) =>
  v3 ? SORTERINGEN.map((s) => (s.waarde === "bronnen" ? { waarde: "bewijs", label: "Sterkste bewijs", uitleg: "Bewijspunten van de opstellers samen (drempel 3), daarna het aantal bronnen." } : s)) : SORTERINGEN;

// ── Filters ────────────────────────────────────────────────────────────────

export type Filters = {
  soort: Soort;
  sector: string[];
  categorie: string[];
  onderwerp: string[];
  signaal: string[];
  niveau: string[];
};
/** De beginstand en de stand na "Alles wissen": de startsector van de
 *  gebruiker (huisregel 18, owner 14-10, K4; sinds ADR 2026-10-23-ict-los-
 *  platform de eigen propositie, zolang alleen ICT zichtbaar is ICT). Andere
 *  sectoren blijven te kiezen in het filter. */
export const STANDAARD_SECTOR = "ict";
export const legeFilters = (sector: string = STANDAARD_SECTOR): Filters => ({
  soort: "vakgebied", sector: [sector], categorie: [], onderwerp: [], signaal: [], niveau: [],
});
export const LEGE_FILTERS: Filters = legeFilters();

/** Voor accountmanager en business partner staat de propositie vast: wat er ook
 *  gekozen of voorgezet wordt, de eigen propositie blijft in de sectorlijst. */
export const metVasteSector = (f: Filters): Filters => {
  const vast = vastePropositie();
  return vast && !f.sector.includes(vast) ? { ...f, sector: [vast, ...f.sector] } : f;
};
const isStandaardSector = (s: readonly string[], standaard: string) => s.length === 1 && s[0] === standaard;

/** Hoeveel filters er afwijken van de beginstand (soort Vaktrends, de startsector).
 *  Zoeken staat los van de filters (huisregel 6) en telt hier niet mee. */
export const aantalFilters = (f: Filters, standaard: string = STANDAARD_SECTOR): number =>
  (f.soort !== "vakgebied" ? 1 : 0) + (isStandaardSector(f.sector, standaard) ? 0 : f.sector.length) + f.categorie.length + f.onderwerp.length + f.signaal.length + f.niveau.length;

export const wissel = <T>(lijst: readonly T[], w: T): T[] => (lijst.includes(w) ? lijst.filter((x) => x !== w) : [...lijst, w]);

// ── Woorden en getallen ────────────────────────────────────────────────────

export const getal = (n: number): string => n.toLocaleString("nl-NL");
export const meervoud = (n: number, een: string, meer: string) => `${getal(n)} ${n === 1 ? een : meer}`;
export const titel = (t: Pick<Trend, "title" | "displayTitle">): string => t.displayTitle || t.title;

export function datum(iso: string | null | undefined): string {
  if (!iso) return "datum onbekend";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "datum onbekend" : d.toLocaleDateString("nl-NL", { month: "short", year: "numeric" });
}

export function kwartaal(iso: string | null | undefined): string {
  if (!iso) return "zonder datum";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "zonder datum" : `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
}

export function isNieuw(iso: string | undefined): boolean {
  if (!iso) return false;
  const d = Date.parse(iso);
  return !Number.isNaN(d) && (Date.now() - d) / 864e5 <= 60;
}

/** De bewijslijst op datum, oud naar nieuw: zo verwijzen de {n}-markers. */
export function opDatum(evidence: readonly Bewijsregel[]): Bewijsregel[] {
  return [...evidence].sort((a, b) => (a.date ? Date.parse(a.date) : Infinity) - (b.date ? Date.parse(b.date) : Infinity));
}

/** Haalt markdown-escapes uit een citaat (backslash vóór [ ] ( ) * _). */
export const schoon = (t: string): string => t.replace(/\\([[\]()*_])/g, "$1");

const WIE: Record<string, string> = { instituut: "instituut", vakmedia: "vakblad", bedrijf: "bedrijf" };

/** Een bewijsregel als bron voor de bouwstenen (BronVermelding, BronnenLijst). */
/** Met `onBron` opent "Bekijk de bron" een laag bovenop het paneel
 *  (huisregel 4); zonder gaat het naar de Bronnen-tab. */
export function alsBron(b: Bewijsregel, onBron?: (b: Bewijsregel) => void): BronItem {
  return {
    sleutel: b.id,
    uitgever: b.publisher,
    titel: b.title || undefined,
    datum: datum(b.date),
    citaat: b.quote ? schoon(b.quote) : undefined,
    soort: b.achterBetaalmuur ? "betaalmuur" : b.wie ? WIE[b.wie] : undefined,
    url: b.url ?? undefined,
    documentId: b.sourceId ?? undefined,
    onKies: b.sourceId ? (onBron ? () => onBron(b) : () => naarTab({ soort: "paneel", tab: "sources", id: b.sourceId! })) : undefined,
  };
}

// ── Het ene label in de regel ──────────────────────────────────────────────

/** "Rustig, met een label" (owner): het stempel van de weging in één woord.
 *  Met de samenvoeglaag het label van de ontwikkeling; zonder bewijs geen label. */
export function labelVan(t: Trend, { inRegel = false }: { inRegel?: boolean } = {}): Label | null {
  const sv = samenvoegingVan(t);
  const l = sv?.label ?? sv?.hoortBij?.label;
  if (l) {
    if (!l.tekst || (inRegel && l.soort === "gesignaleerd")) return null;
    return { tekst: l.tekst, toon: samenvoegToon(l.soort), uitleg: l.regel ?? l.uitleg ?? undefined };
  }
  const b = t.bewijs;
  if (!b || !b.laag) return null;
  // De uitleg in losse delen, zonder herhaling (de stempeltekst en de
  // verplichtingszin zijn vaak dezelfde zin).
  const delen = [b.tekst, b.ouderBewijs ? "ouder bewijs" : null, b.verplichting ? b.verplichtingUitleg : null, mistTekst(b)].filter((d): d is string => !!d);
  const uitleg = [...new Set(delen)].filter((d, i, a) => !a.some((x, j) => j !== i && x.includes(d) && x !== d)).join(" · ");
  if (b.laag === "bevestigd" || b.laag === "verplichting") return { tekst: b.verplichting || b.laag === "verplichting" ? "Verplichting" : "Bevestigd", toon: "groen", uitleg };
  if (b.laag === "advies") return { tekst: "Advies", toon: "blauw", uitleg };
  // Gesignaleerd is de grondtoon van de lijst: in een regel geen label (rustig),
  // in het paneel wel.
  return inRegel ? null : { tekst: "Gesignaleerd", toon: "neutraal", uitleg };
}

/** De meta onder de titel: niveau · bronnen (· nieuw). */
export function metaVan(t: Trend, extra?: string | null): string {
  return [niveauWoord(t.level), meervoud(t.sourceCount, "bron", "bronnen"), extra].filter(Boolean).join(" · ");
}
